import { Alert, Button, Radio, Select, Tabs, TreeSelect, Typography } from "@arco-design/web-react";
import { useState } from "react";

import { MAX_FLOW_STEPS, MAX_STEP_MEMBERS } from "../config";
import type { useApprovalSettingsRuntime } from "../runtime/useApprovalSettingsRuntime";
import { BRANCH_DIMENSION_LABELS, MAIN_FLOW, flowLabel, flowLabelInText, flowSteps, formatMemberName } from "../service/approvalSettingsService";
import type { ApprovalBranchDimension, ApprovalBranchDraft } from "../types";
import { ApprovalMemberPicker } from "./ApprovalMemberPicker";
import { ApprovalStepList } from "./ApprovalStepList";

type Runtime = ReturnType<typeof useApprovalSettingsRuntime>;
type ApprovalFlowEditorProps = { runtime: Runtime };

/** 一种审批的流程编辑：主流程与分支 1～3 切换编辑，全部错误汇总在顶部、并写在对应位置。 */
export function ApprovalFlowEditor({ runtime }: ApprovalFlowEditorProps) {
  const [activeFlow, setActiveFlow] = useState(MAIN_FLOW);
  const [picking, setPicking] = useState<{ flow: number; index: number } | null>(null);
  const draft = runtime.draft;
  if (!draft) return null;
  const pickingSteps = picking ? flowSteps(draft, picking.flow) : [];
  const pickingStep = picking ? pickingSteps[picking.index] : undefined;
  return (
    <div className="approval-flow-editor">
      <Alert
        type="info"
        content={`分支按顺序比对，命中第一条就走它；都不命中走主流程。改设置只影响之后提交的单子。每一级二选一：指定 1～${MAX_STEP_MEMBERS} 名成员（其中一人同意即进入下一级），或申请人所选部门的审批人；每条流程最多 ${MAX_FLOW_STEPS} 级。`}
      />
      {runtime.errorMessage && (
        <Alert
          type="error"
          content={runtime.errorMessage}
          action={runtime.conflict ? <Button size="mini" onClick={runtime.reloadAfterConflict}>刷新</Button> : undefined}
        />
      )}
      {runtime.errors.messages.length > 0 && (
        <Alert type="error" content={<ul className="approval-error-list">{runtime.errors.messages.map((message) => <li key={message}>{message}</li>)}</ul>} />
      )}
      <Tabs activeTab={String(activeFlow)} onChange={(key) => setActiveFlow(Number(key))}>
        <Tabs.TabPane key={String(MAIN_FLOW)} title="主流程">
          <div className="approval-flow-pane">
            {!draft.mainFlow.length && <Typography.Text type="warning">主流程未设置：没命中分支的单子提交会失败。</Typography.Text>}
            <ApprovalStepList runtime={runtime} flow={MAIN_FLOW} onPick={(index) => setPicking({ flow: MAIN_FLOW, index })} />
          </div>
        </Tabs.TabPane>
        {draft.branches.map((branch, index) => {
          const branchNo = index + 1;
          return (
            <Tabs.TabPane key={String(branchNo)} title={flowLabel(branchNo)}>
              {branch ? (
                <BranchPane runtime={runtime} branchNo={branchNo} branch={branch} onPick={(stepIndex) => setPicking({ flow: branchNo, index: stepIndex })} />
              ) : (
                <div className="approval-branch-unused">
                  <Typography.Text type="secondary">{flowLabel(branchNo)} 未使用。</Typography.Text>
                  <Button type="outline" onClick={() => runtime.enableBranch(branchNo)}>使用{flowLabel(branchNo)}</Button>
                </div>
              )}
            </Tabs.TabPane>
          );
        })}
      </Tabs>
      <ApprovalMemberPicker
        title={picking ? `选择${flowLabelInText(picking.flow)}第 ${picking.index + 1} 级审批人` : null}
        tree={runtime.memberTree}
        selectedIds={pickingStep?.memberIds ?? []}
        loading={runtime.candidatesLoading}
        errorMessage={runtime.candidatesError}
        onRetry={runtime.retryCandidates}
        onChange={(memberIds) => { if (picking) runtime.setStepMembers(picking.flow, picking.index, memberIds); }}
        onClose={() => setPicking(null)}
      />
    </div>
  );
}

type BranchPaneProps = { runtime: Runtime; branchNo: number; branch: ApprovalBranchDraft; onPick: (index: number) => void };

/** 一条在用的分支：范围维度、按维度选结算主体或部门、逐级卡片，可清空整条分支。 */
function BranchPane({ runtime, branchNo, branch, onPick }: BranchPaneProps) {
  const label = flowLabel(branchNo);
  const scopeError = runtime.errors.flows[branchNo]?.scopeError;
  const named = (name: string | undefined, fallback: string, disabled: boolean | undefined) => formatMemberName(name ?? fallback, disabled ?? false);
  const selectedSuppliers = branch.supplierIds.map((supplierId) => {
    const item = runtime.supplierLookup.get(supplierId);
    return { value: supplierId, label: named(item?.name, `结算主体 ${supplierId}`, item?.disabled) };
  });
  const supplierOptions = [
    ...selectedSuppliers,
    ...runtime.supplierResults
      .filter((item) => !branch.supplierIds.includes(item.supplierId))
      .map((item) => ({ value: item.supplierId, label: item.name })),
  ];
  return (
    <div className="approval-flow-pane">
      <div className="approval-branch-scope-bar">
        <Radio.Group
          aria-label={`${label}的范围维度`}
          value={branch.scopeDimension}
          options={(runtime.draft?.branchDimensions ?? []).map((dimension) => ({ label: `按${BRANCH_DIMENSION_LABELS[dimension]}`, value: dimension }))}
          onChange={(value: ApprovalBranchDimension) => runtime.setBranchDimension(branchNo, value)}
        />
        <Button size="mini" status="danger" onClick={() => runtime.clearBranch(branchNo)}>清空{label}</Button>
      </div>
      {branch.scopeDimension === "settlement_supplier" ? (
        <Select
          mode="multiple"
          showSearch
          allowClear
          filterOption={false}
          aria-label={`${label}的结算主体`}
          placeholder="输入名称搜索结算主体"
          value={branch.supplierIds}
          options={supplierOptions}
          loading={runtime.suppliersLoading}
          onSearch={runtime.searchSuppliers}
          onChange={(supplierIds: string[]) => runtime.setBranchSuppliers(branchNo, supplierIds)}
        />
      ) : (
        <TreeSelect
          treeCheckable
          treeCheckStrictly
          labelInValue
          showSearch
          allowClear
          aria-label={`${label}的部门`}
          placeholder="勾选部门（只算勾到的部门，不含下级）"
          treeData={runtime.departmentTree}
          loading={runtime.candidatesLoading}
          value={branch.departmentIds.map((deptId) => {
            const item = runtime.departmentLookup.get(deptId);
            return { value: String(deptId), label: named(item?.name, `部门 ${deptId}`, item?.disabled) };
          })}
          filterTreeNode={(input: string, node: { props: { title?: unknown } }) => String(node.props.title ?? "").includes(input.trim())}
          onChange={(values: Array<{ value: string } | string> | undefined) =>
            runtime.setBranchDepartments(branchNo, (values ?? []).map((item) => Number(typeof item === "string" ? item : item.value)))}
        />
      )}
      {runtime.suppliersError && branch.scopeDimension === "settlement_supplier" && <Typography.Text type="error">{runtime.suppliersError}</Typography.Text>}
      {scopeError && <Typography.Text type="error">{scopeError}</Typography.Text>}
      <ApprovalStepList runtime={runtime} flow={branchNo} onPick={onPick} />
    </div>
  );
}
